/**
 * CorrezioniTimbrature — le richieste di correzione delle proprie
 * timbrature (Alex, 22/10/2026; migration 339).
 *
 * TRE PEZZI, UNA REGOLA SOLA, CHE NON STA QUI
 *   - MieCorrezioni: l'elenco delle proprie richieste in «Le mie ore», con
 *     lo stato, il motivo del rifiuto e «Ritira» per quelle in attesa;
 *   - ModuloCorrezione: il modulo per chiederne una (aggiungere un'entrata o
 *     un'uscita mancante, o cambiare l'orario di una timbratura), col motivo
 *     obbligatorio. E' un gesto che crea una riga: un pulsante, non un campo
 *     che si salva da solo;
 *   - RichiesteCorrezione: le richieste per l'amministrazione, in Presenze,
 *     con «Approva» e «Rifiuta».
 *
 * Le regole - chi chiede, per quali giorni, entro quanti, chi decide - le
 * decide il server (timbratura_richiesta_rifiuto e
 * timbratura_richiesta_chi_decide, nel database). Qui non si rifanno: il
 * modulo propone i giorni del limite che il server manda, e «Approva» e
 * «Rifiuta» compaiono solo dove il server dice puo_decidere. Chi chiede non
 * decide (Alex, 22/10/2026, conferma 5): il pulsante non c'e' sulle proprie
 * richieste, ma il controllo vero e' il 403 della rotta.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Check, X, Send, ThumbsUp, ThumbsDown, PencilLine } from 'lucide-react';
import { presenzeAPI } from '../../services/personaleApi';
import ErroreLettura from '../comuni/ErroreLettura';
import { conUnTentativo, motivoDellaLettura } from '../../utils/primaLettura';
import { oggiClinica, oraIta } from '../../utils/oraItaliana';

const messaggio = (e, ripiego) => e?.response?.data?.message || e?.message || ripiego;

/* Le parole degli stati: il server manda il codice. «annullata» e' la
   richiesta ritirata da chi l'aveva fatta. */
export const STATI_CORREZIONE = {
    in_attesa: { t: 'In attesa', c: 'attesa' },
    approvata: { t: 'Approvata', c: 'buono' },
    rifiutata: { t: 'Rifiutata', c: 'guasto' },
    annullata: { t: 'Ritirata', c: '' },
};

/** «2026-10-15» -> «15/10/2026», senza passare dal fuso del browser. */
export const dataGiorno = (g) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(g || ''));
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};

/** «2026-10-15 17:30» -> «15/10/2026 17:30». */
const momentoIta = (s) => {
    const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2})/.exec(String(s || ''));
    return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}` : '—';
};

const oraDi = (s) => (/ (\d{2}:\d{2})/.exec(String(s || '')) || [])[1] || '—';

/** Un giorno «AAAA-MM-GG» spostato di n giorni, in UTC: non dipende dal
 *  fuso della postazione. */
export const giornoPiu = (g, n) => {
    const [a, m, d] = String(g).slice(0, 10).split('-').map(Number);
    return new Date(Date.UTC(a, m - 1, d + n)).toISOString().slice(0, 10);
};

/** I giorni in cui la dipendente puo' chiedere: da oggi meno N a ieri. La
 *  regola vera e' del server; qui serve a proporre solo quelli. */
export const finestraRichieste = (entroGiorni, oggi = oggiClinica()) => (
    Number.isInteger(entroGiorni) && entroGiorni > 0
        ? { dal: giornoPiu(oggi, -entroGiorni), al: giornoPiu(oggi, -1) }
        : null
);

/** Che cosa chiede una richiesta, in una frase. */
export const cosaChiede = (r) => {
    const una = r.tipo === 'entrata' ? 'un\'entrata' : 'un\'uscita';
    const della = r.tipo === 'entrata' ? 'dell\'entrata' : 'dell\'uscita';
    return r.azione === 'aggiungi'
        ? `Aggiungere ${una} alle ${oraDi(r.momento)}`
        : `Cambiare l'orario ${della} dalle ${oraDi(r.momento_originale)} alle ${oraDi(r.momento)}`;
};

/* ================================================================== */
/* La dipendente: le sue richieste                                     */
/* ================================================================== */

export function MieCorrezioni({ richieste, entroGiorni, inEvidenza, onChiedi, onRitira }) {
    const finestra = finestraRichieste(entroGiorni);
    return (
        <div className="adp-correzioni" data-correzioni="mie">
            <div className="adp-correzioni-testa">
                <h3 className="adp-sezione">Le mie richieste di correzione</h3>
                <button className="adp-btn adp-btn--principale" onClick={() => onChiedi(null)}
                        disabled={!finestra} data-azione="chiedi-correzione">
                    <PencilLine size={15} /> Chiedi una correzione
                </button>
            </div>
            <p className="adp-nota">
                {finestra
                    ? <>Una correzione si chiede per i giorni passati, entro {entroGiorni}{' '}
                        {entroGiorni === 1 ? 'giorno' : 'giorni'} dalla data: per oggi la timbratura
                        dimenticata si inserisce dal tablet, con «Ho dimenticato di timbrare». La
                        richiesta resta in attesa finché l&apos;amministrazione non decide, e
                        finché è in attesa la puoi ritirare.</>
                    : <>Il limite di giorni per chiedere una correzione non è impostato
                        correttamente: avvisa l&apos;amministrazione.</>}
            </p>
            {!richieste.length ? (
                <p className="adp-vuoto">Nessuna richiesta di correzione.</p>
            ) : (
                <div className="rm-scorre">
                    <table className="adp-tabella">
                        <thead>
                            <tr>
                                <th>Giorno</th><th>Richiesta</th><th>Motivo</th>
                                <th>Stato</th><th>Decisione</th><th />
                            </tr>
                        </thead>
                        <tbody>
                            {richieste.map((r) => {
                                const st = STATI_CORREZIONE[r.stato] || {};
                                return (
                                    <tr key={r.id} data-richiesta={r.id}
                                        className={r.id === inEvidenza ? 'adp-in-evidenza' : undefined}>
                                        <td>{dataGiorno(r.giorno)}</td>
                                        <td>{cosaChiede(r)}</td>
                                        <td className="adp-sub">{r.motivo}</td>
                                        <td><span className={`adp-pillola ${st.c}`}>{st.t || r.stato}</span></td>
                                        <td className="adp-sub">
                                            {r.stato === 'rifiutata' && (
                                                <div className="adp-motivo-rifiuto">
                                                    Motivo del rifiuto: «{r.motivo_rifiuto}»
                                                </div>
                                            )}
                                            {r.decisa_il && r.stato !== 'annullata' && (
                                                <div>{r.decisa_da_nome || '—'}, {momentoIta(r.decisa_il)}</div>
                                            )}
                                            {r.stato === 'annullata' && <div>Ritirata il {momentoIta(r.decisa_il)}</div>}
                                        </td>
                                        <td className="num">
                                            {r.stato === 'in_attesa' && (
                                                <button className="adp-btn adp-btn--piccolo"
                                                        onClick={() => onRitira(r)} data-azione="ritira">
                                                    <X size={14} /> Ritira
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/* ================================================================== */
/* La dipendente: il modulo                                            */
/* ================================================================== */

/**
 * `giorno` proposto (dalla riga di «Le mie ore») o vuoto. Le timbrature del
 * giorno per «Cambia l'orario» si chiedono al registro, che a chi non e'
 * amministrazione da' solo le proprie: le valide soltanto, perche' una
 * timbratura gia' corretta o annullata non si corregge di nuovo.
 */
export function ModuloCorrezione({ giorno, entroGiorni, dipendenteId, onChiudi, onInviata }) {
    const finestra = finestraRichieste(entroGiorni);
    const [f, setF] = useState({
        giorno: giorno && finestra && giorno >= finestra.dal && giorno <= finestra.al ? giorno : '',
        azione: 'aggiungi', tipo: 'uscita', ora: '', timbratura_id: '', motivo: '',
    });
    const [timbrature, setTimbrature] = useState(null);
    const [errore, setErrore] = useState(null);
    const [inCorso, setInCorso] = useState(false);

    useEffect(() => {
        let vivo = true;
        setTimbrature(null);
        if (!f.giorno) return undefined;
        presenzeAPI.timbrature(f.giorno, f.giorno, dipendenteId)
            .then((r) => {
                if (vivo) setTimbrature((r.data.data.timbrature || []).filter((t) => t.valida));
            })
            .catch(() => { if (vivo) setTimbrature([]); });
        return () => { vivo = false; };
    }, [f.giorno, dipendenteId]);

    const scelta = (timbrature || []).find((t) => String(t.id) === String(f.timbratura_id));

    const invia = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            const corpo = f.azione === 'aggiungi'
                ? { azione: 'aggiungi', tipo: f.tipo, momento: `${f.giorno} ${f.ora}`, motivo: f.motivo }
                : { azione: 'cambia_orario', timbratura_id: Number(f.timbratura_id),
                    momento: `${f.giorno} ${f.ora}`, motivo: f.motivo };
            const r = await presenzeAPI.richiediCorrezione(corpo);
            onInviata(r.data.message);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nell\'invio della richiesta'));
        } finally {
            setInCorso(false);
        }
    };

    const pronta = f.giorno && f.ora && f.motivo.trim()
        && (f.azione === 'aggiungi' || f.timbratura_id);

    return (
        <div className="adp-sfondo" onClick={onChiudi}>
            <div className="adp-modale" role="dialog" aria-modal="true" aria-label="Chiedi una correzione"
                 onClick={(e) => e.stopPropagation()} data-modulo="correzione">
                <div className="adp-modale-testa">
                    <h2><PencilLine size={17} /> Chiedi una correzione</h2>
                    <button onClick={onChiudi} aria-label="Chiudi"><X size={17} /></button>
                </div>
                {errore && <p className="adp-errore">{errore}</p>}
                <label className="adp-campo">
                    <span>Giorno *</span>
                    <input type="date" name="giorno" value={f.giorno}
                           min={finestra?.dal} max={finestra?.al}
                           onChange={(e) => setF({ ...f, giorno: e.target.value, timbratura_id: '' })} />
                </label>
                <label className="adp-campo">
                    <span>Che cosa va corretto *</span>
                    <select name="azione" value={f.azione}
                            onChange={(e) => setF({ ...f, azione: e.target.value, timbratura_id: '' })}>
                        <option value="aggiungi">Manca una timbratura: aggiungila</option>
                        <option value="cambia_orario">{'Una timbratura ha l\'orario sbagliato'}</option>
                    </select>
                </label>
                {f.azione === 'aggiungi' ? (
                    <label className="adp-campo">
                        <span>Entrata o uscita *</span>
                        <select name="tipo" value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })}>
                            <option value="entrata">Entrata</option>
                            <option value="uscita">Uscita</option>
                        </select>
                    </label>
                ) : (
                    <label className="adp-campo">
                        <span>La timbratura da correggere *</span>
                        <select name="timbratura" value={f.timbratura_id} disabled={!f.giorno}
                                onChange={(e) => setF({ ...f, timbratura_id: e.target.value })}>
                            <option value="">
                                {!f.giorno ? 'Scegli prima il giorno'
                                 : timbrature === null ? 'Caricamento…'
                                 : timbrature.length ? 'Scegli…' : 'Nessuna timbratura in questo giorno'}
                            </option>
                            {(timbrature || []).map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.tipo === 'entrata' ? 'Entrata' : 'Uscita'} delle {oraIta(t.momento)}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
                <label className="adp-campo">
                    <span>{f.azione === 'aggiungi' ? 'Ora *' : 'Ora giusta *'}</span>
                    <input type="time" name="ora" value={f.ora} onChange={(e) => setF({ ...f, ora: e.target.value })} />
                </label>
                {scelta && (
                    <p className="adp-nota">
                        {scelta.tipo === 'entrata' ? 'Entrata' : 'Uscita'} timbrata alle {oraIta(scelta.momento)}:
                        si cambia solo l&apos;orario, nello stesso giorno.
                    </p>
                )}
                <label className="adp-campo">
                    <span>Motivo *</span>
                    <textarea rows="2" name="motivo" value={f.motivo}
                              placeholder="Che cosa va corretto e perché"
                              onChange={(e) => setF({ ...f, motivo: e.target.value })} />
                </label>
                <p className="adp-nota">
                    La richiesta resta <b>in attesa</b> finché l&apos;amministrazione non decide.
                    La timbratura di prima non si cancella: se la richiesta è approvata, la
                    correzione resta scritta con chi l&apos;ha chiesta e chi l&apos;ha approvata.
                </p>
                <div className="adp-modale-fondo">
                    <button className="adp-btn" onClick={onChiudi}>Annulla</button>
                    <button className="adp-btn adp-btn--principale" onClick={invia}
                            disabled={inCorso || !pronta} data-azione="invia-correzione">
                        <Send size={15} /> Invia la richiesta
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ================================================================== */
/* L'amministrazione: le richieste da decidere                        */
/* ================================================================== */

const FILTRI = [
    ['in_attesa', 'In attesa'], ['approvata', 'Approvate'], ['rifiutata', 'Rifiutate'],
    ['annullata', 'Ritirate'], ['tutte', 'Tutte'],
];

export function RichiesteCorrezione({ inEvidenza, onDecisa, onAvviso, onErrore }) {
    const [filtro, setFiltro] = useState('in_attesa');
    const [righe, setRighe] = useState(null);
    const [rifiuto, setRifiuto] = useState(null);
    const [inCorso, setInCorso] = useState(false);
    /* Arrivati da un avviso su una richiesta gia' decisa: si guardano tutte,
       una volta sola. */
    const [cercata, setCercata] = useState(false);
    /* L'errore della lettura dell'elenco sta qui, con «Riprova», e la
       lettura riuscita lo toglie; quelli delle decisioni vanno al genitore
       come prima (primaLettura.js). */
    const [erroreLettura, setErroreLettura] = useState(null);

    const carica = useCallback(async (stato = filtro) => {
        try {
            const r = await conUnTentativo(() => presenzeAPI.correzioni(stato));
            setRighe(r.data.data.richieste || []);
            setErroreLettura(null);
            return r.data.data.richieste || [];
        } catch (e) {
            setRighe([]);
            setErroreLettura(motivoDellaLettura(e, 'Impossibile caricare le richieste di correzione'));
            return [];
        }
    }, [filtro]);

    useEffect(() => { carica(filtro); }, [filtro, carica]);

    useEffect(() => {
        if (!inEvidenza || cercata || righe === null) return;
        setCercata(true);
        if (filtro === 'in_attesa' && !righe.some((r) => r.id === inEvidenza)) setFiltro('tutte');
    }, [inEvidenza, cercata, righe, filtro]);

    const approva = async (r) => {
        try {
            onErrore(null);
            setInCorso(true);
            let risposta;
            try {
                risposta = await presenzeAPI.decidiCorrezione(r.id, { decisione: 'approvata' });
            } catch (e1) {
                /* Il mese chiuso: la stessa conferma delle correzioni dirette. */
                if (e1?.response?.data?.codice !== 'mese_chiuso') throw e1;
                if (!window.confirm(`${e1.response.data.message}\n\n`
                    + 'La correzione resta nel registro con il suo motivo, come tutte le altre.')) return;
                risposta = await presenzeAPI.decidiCorrezione(r.id, { decisione: 'approvata', forza: true });
            }
            onAvviso(risposta.data.message);
            await carica();
            onDecisa();
        } catch (e) {
            onErrore(messaggio(e, 'Errore nella decisione'));
            await carica();
        } finally {
            setInCorso(false);
        }
    };

    const rifiuta = async () => {
        try {
            onErrore(null);
            setInCorso(true);
            const r = await presenzeAPI.decidiCorrezione(rifiuto.id, {
                decisione: 'rifiutata', motivo_rifiuto: rifiuto.motivo_rifiuto,
            });
            onAvviso(r.data.message);
            setRifiuto(null);
            await carica();
            onDecisa();
        } catch (e) {
            onErrore(messaggio(e, 'Errore nella decisione'));
        } finally {
            setInCorso(false);
        }
    };

    const inAttesa = (righe || []).filter((r) => r.stato === 'in_attesa').length;

    return (
        <div className="adp-blocco adp-correzioni" data-correzioni="amministrazione">
            <div className="adp-blocco-testa">
                <h3>Richieste di correzione</h3>
                <span>
                    {righe === null ? 'Caricamento…'
                     : filtro === 'in_attesa'
                         ? (inAttesa ? `${inAttesa} in attesa` : 'Nessuna richiesta in attesa')
                         : `${righe.length} nell'elenco`}
                </span>
                <select value={filtro} onChange={(e) => setFiltro(e.target.value)}
                        data-non-trattiene aria-label="Quali richieste di correzione">
                    {FILTRI.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                </select>
            </div>
            <ErroreLettura testo={erroreLettura} onRiprova={() => carica(filtro)} classe="adp-errore" />
            {righe && righe.length > 0 && (
                <div className="rm-scorre">
                    <table className="adp-tabella">
                        <thead>
                            <tr>
                                <th>Giorno</th><th>Dipendente</th><th>Richiesta</th><th>Motivo</th>
                                <th>Chiesta il</th><th>Stato</th><th />
                            </tr>
                        </thead>
                        <tbody>
                            {righe.map((r) => {
                                const st = STATI_CORREZIONE[r.stato] || {};
                                return (
                                    <tr key={r.id} data-richiesta={r.id}
                                        className={r.id === inEvidenza ? 'adp-in-evidenza' : undefined}>
                                        <td>
                                            {dataGiorno(r.giorno)}
                                            {r.mese_chiuso && <span className="adp-sub">mese chiuso</span>}
                                        </td>
                                        <td>{r.nominativo}</td>
                                        <td>{cosaChiede(r)}</td>
                                        <td className="adp-sub">{r.motivo}</td>
                                        <td className="adp-sub">{momentoIta(r.richiesta_il)}</td>
                                        <td>
                                            <span className={`adp-pillola ${st.c}`}>{st.t || r.stato}</span>
                                            {r.stato === 'rifiutata' && (
                                                <span className="adp-sub">«{r.motivo_rifiuto}»</span>
                                            )}
                                            {r.decisa_il && r.stato !== 'annullata' && (
                                                <span className="adp-sub">{r.decisa_da_nome || '—'}, {momentoIta(r.decisa_il)}</span>
                                            )}
                                        </td>
                                        <td className="adp-correzioni-azioni">
                                            {r.puo_decidere ? (
                                                <>
                                                    <button className="adp-btn adp-btn--piccolo" disabled={inCorso}
                                                            onClick={() => approva(r)} data-azione="approva">
                                                        <ThumbsUp size={14} /> Approva
                                                    </button>
                                                    <button className="adp-btn adp-btn--piccolo" disabled={inCorso}
                                                            onClick={() => setRifiuto({ ...r, motivo_rifiuto: '' })}
                                                            data-azione="rifiuta">
                                                        <ThumbsDown size={14} /> Rifiuta
                                                    </button>
                                                </>
                                            ) : r.non_decide_perche ? (
                                                <span className="adp-sub" data-non-decide>{r.non_decide_perche}</span>
                                            ) : null}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {rifiuto && (
                <div className="adp-sfondo" onClick={() => setRifiuto(null)}>
                    <div className="adp-modale" role="dialog" aria-modal="true" aria-label="Rifiuta la richiesta"
                         onClick={(e) => e.stopPropagation()} data-modulo="rifiuto-correzione">
                        <div className="adp-modale-testa">
                            <h2><ThumbsDown size={17} /> Rifiuta la richiesta</h2>
                            <button onClick={() => setRifiuto(null)} aria-label="Chiudi"><X size={17} /></button>
                        </div>
                        <p className="adp-riepilogo-testo">
                            <b>{rifiuto.nominativo}</b> — {dataGiorno(rifiuto.giorno)}: {cosaChiede(rifiuto)}.
                        </p>
                        <label className="adp-campo">
                            <span>Motivo del rifiuto *</span>
                            <textarea rows={3} name="motivo_rifiuto" value={rifiuto.motivo_rifiuto}
                                      placeholder="Lo legge chi ha fatto la richiesta"
                                      onChange={(e) => setRifiuto({ ...rifiuto, motivo_rifiuto: e.target.value })} />
                        </label>
                        <p className="adp-nota">
                            La dipendente riceve un avviso con questo motivo, e lo ritrova
                            accanto alla sua richiesta. Le timbrature non cambiano.
                        </p>
                        <div className="adp-modale-fondo">
                            <button className="adp-btn" onClick={() => setRifiuto(null)}>Annulla</button>
                            <button className="adp-btn adp-btn--principale" onClick={rifiuta}
                                    disabled={inCorso || !rifiuto.motivo_rifiuto.trim()} data-azione="conferma-rifiuto">
                                <Check size={15} /> Rifiuta
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
